/**
 * Round half away from zero without binary floating-point surprises
 * (plain toFixed turns 1.005 into "1.00").
 */
export function roundTo(value: number, decimals: number): number {
  if (!Number.isFinite(value)) return value;
  const sign = value < 0 ? -1 : 1;
  const shifted = Number(`${Math.abs(value)}e${decimals}`);
  return sign * Number(`${Math.round(shifted)}e-${decimals}`);
}

export function formatNumber(value: number, decimals?: number): string {
  if (decimals === undefined) return String(value);
  return roundTo(value, decimals).toFixed(decimals);
}

/** Indian grouping: 130000 → "1,30,000". */
export function formatIndian(value: number, decimals = 0): string {
  const [int = '0', frac] = formatNumber(Math.abs(value), decimals).split('.');
  const last3 = int.slice(-3);
  const rest = int.slice(0, -3);
  const grouped = rest ? `${rest.replace(/\B(?=(\d{2})+(?!\d))/g, ',')},${last3}` : last3;
  return `${value < 0 ? '-' : ''}${grouped}${frac ? `.${frac}` : ''}`;
}

export interface Age {
  years: number;
  months: number;
  days: number;
  totalDays: number;
}

/** Age on a given date (both YYYY-MM-DD), as calendar years/months/days. */
export function ageOn(dob: string, on: string): Age {
  const b = new Date(`${dob}T00:00:00Z`);
  const d = new Date(`${on}T00:00:00Z`);
  if (Number.isNaN(b.getTime()) || Number.isNaN(d.getTime()) || d < b) {
    throw new RangeError(`Invalid dates: dob=${dob} on=${on}`);
  }
  let years = d.getUTCFullYear() - b.getUTCFullYear();
  let months = d.getUTCMonth() - b.getUTCMonth();
  let days = d.getUTCDate() - b.getUTCDate();
  if (days < 0) {
    months -= 1;
    days += new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0)).getUTCDate();
  }
  if (months < 0) {
    years -= 1;
    months += 12;
  }
  const totalDays = Math.round((d.getTime() - b.getTime()) / 86_400_000);
  return { years, months, days, totalDays };
}

/** How Indian reports print age: "36 Y", "8 M", "12 D". */
export function formatAge(age: Pick<Age, 'years' | 'months' | 'days'>): string {
  if (age.years >= 1) return `${age.years} Y`;
  if (age.months >= 1) return `${age.months} M`;
  return `${age.days} D`;
}

/** Approximate days for an age typed without a date of birth ("36 Y"). */
export function ageToDays(amount: number, unit: 'Y' | 'M' | 'D'): number {
  if (unit === 'Y') return Math.round(amount * 365.25);
  if (unit === 'M') return Math.round(amount * 30.4375);
  return Math.round(amount);
}

const IST = 'Asia/Kolkata';

/** "31/08/2026 10:20 AM" in Indian time, the way lab reports print it. */
export function formatDateTimeIST(iso: string | Date | undefined | null): string {
  if (!iso) return '';
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: IST,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('day')}/${get('month')}/${get('year')} ${get('hour')}:${get('minute')} ${get('dayPeriod').toUpperCase()}`;
}

export function formatDateIST(iso: string | Date | undefined | null): string {
  return formatDateTimeIST(iso).split(' ')[0] ?? '';
}
